import React, { useEffect, useRef, useState } from "react";
import dots from "../icons/dots-red.svg";
import Dropdown from "../icons/dropdown.svg";
import {
  AsyncSelect,
  Button,
  Checkbox,
  EditableText,
  FlashMessage,
  Input,
  Modal,
} from "../../common";
import {
  FormControlLabel,
  Grid,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import LocationIcon from "../icons/location-select.svg";
import MapPinIcon from "../icons/map-pin-drop.svg";
import NameIcon from "../icons/name-icon.svg";
import EmailIcon from "../icons/email-icon.svg";
import PhoneIcon from "../icons/phone-icon.svg";
import JobTitleIcon from "../icons/job-title-icon.svg";
import GenderIcon from "../icons/gender-select-icon.svg";
import copyToClipboard from "copy-to-clipboard";
import { useForm } from "react-hook-form";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import {
  GENDER_SELECT_OPTIONS,
  getGenderSelectOption,
  getRemainingResendHours,
  getGenderLabelFromProfile,
  isCustomGender,
  sanitizeBody,
  storeNewMessageInfo,
  validatePhoneNumber,
} from "../../../utils/helper";
import { updateBasicInformation, updateCandidateVisibilityRequest, fetchSessionDataRequest } from "../../../reducers/auth/session.reducer";
import { tabChangeRequest } from "../../../reducers/company/tabChange.reducer";
import candidateServices from "../../../services/candidate.services";
import ProfileImage from "./ProfileImage";
import {
  CUSTOM_GENDER_OPTIONS,
  connectionRequestStatus,
  WorkStatusType,
} from "../../../utils/appConstants";
import commonServices from "../../../services/common.services";
import { rootReducersState } from "../../../reducers";
import ProfileShareModal from "../../ProfileView/components/ProfileShareModal";
import Report from "../../common/Report";
import appRoutes from "../../../routes/app.routes";
import { useNavigate } from "react-router-dom";
import InvitationModal from "../../../pages/SearchAndConnect/components/InvitationModal";
import searchAndConnectServices from "../../../services/searchAndConnect.services";
import { canReceiveMessages } from "../../../utils/messaging.utils";

let searchDelay: NodeJS.Timeout;
interface Location {
  id: number | string;
  location: string;
}

interface LocationOption {
  value: number | string;
  label: string;
}

interface CandidateFormData {
  first_name: string;
  last_name: string;
  email: string;
  contact_info: string;
  jobTitle: { value?: number | string; label?: string } | null;
  location: { value?: number | string; label?: string } | null;
  work_status: { value?: number | string; label?: string } | null;
  gender: { value?: string | number; label?: string } | null;
  gender_optional?: string;
  open_to_remote: number;
  open_to_other: number;
  slug?: string;
  gender_pronoun: string | number;
}

interface ProfileImageDataProps {
  profile_image: string;
  profile_video: any;
}
interface CandidateInfoProps {
  profileData: CandidateFormData;
  profileImageData: ProfileImageDataProps;
  publicPage?: boolean;
  candidate?: any;
  openMediaUploadRequest?: boolean;
  onOpenMediaUploadHandled?: () => void;
  openEditRequest?: boolean;
  onOpenEditHandled?: () => void;
}

function CandidateInfo({
  profileData,
  profileImageData,
  publicPage,
  candidate,
  openMediaUploadRequest,
  onOpenMediaUploadHandled,
  openEditRequest,
  onOpenEditHandled,
}: CandidateInfoProps) {
  const genderOptions = [...GENDER_SELECT_OPTIONS];


  const navigate = useNavigate();
  const [profileVisibleDropdown, setProfileVisibleDropdown] =
    useState<boolean>(false);
  const visibilityControlRef = useRef<HTMLDivElement>(null);
  const [visibilityStatus, setVisibilityStatus] = useState(0);
  const [editProfile, setEditProfile] = useState<boolean>(false);
  const [shareProfileModal, setShareProfileModal] = useState<boolean>(false);
  const [invitationOpen, setInvitationOpen] = useState(false);
  const [msgAnchorEl, setMsgAnchorEl] = useState<null | HTMLElement>(null);
  const [publicMenuAnchorEl, setPublicMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [connectStatusId, setConnectStatusId] = useState<number | null>(
    _.get(candidate, "follow.status", null)
  );
  const [connectionActionLoading, setConnectionActionLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    clearErrors,
    unregister,
    trigger,
    formState: { errors, isValid },
  } = useForm<any>({
    mode: "onChange",
    defaultValues: {
      profileData: profileData || {},
    },
  });



  const sessionReducer = useSelector(
    ({ session }: rootReducersState) => session.currentUser
  );
  const sessionUserEmail = _.get(sessionReducer, "email", "");
  const profileUserEmail = _.get(profileData, "email", "");
  const sessionSlug = _.get(sessionReducer, "slug", "") || "";
  const profileSlug =
    _.get(profileData, "slug", "") || _.get(candidate, "slug", "") || "";
  const sessionUserId = _.get(sessionReducer, "id", null);
  const profileUserId = _.get(candidate, "id", null);

  const isOwnProfile =
    (!!sessionUserEmail &&
      !!profileUserEmail &&
      sessionUserEmail === profileUserEmail) ||
    (!!sessionSlug && !!profileSlug && sessionSlug === profileSlug) ||
    (sessionUserId != null &&
      profileUserId != null &&
      String(sessionUserId) === String(profileUserId));

  useEffect(() => {
    setConnectStatusId(_.get(candidate, "follow.status", null));
  }, [candidate]);

  const connectStatusText =
    connectionRequestStatus.find((row) => row.status_id === connectStatusId)
      ?.status_text || "";
  const connectAccepted = connectStatusText === "accepted";
  const profileMessagingEnabled = canReceiveMessages(
    candidate || profileData
  );

  const openProfileModal = () => {
    if (publicPage) return;
    const updatedProjects = watch("profileData") as CandidateFormData;
    reset(updatedProjects as CandidateFormData);
    setEditProfile(true);
  };

  useEffect(() => {
    if (!openEditRequest || publicPage) return;
    openProfileModal();
    onOpenEditHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openEditRequest, publicPage]);

  const closeProfileModal = () => {
    const updatedProjects = watch("profileData") as any;
    reset(updatedProjects as CandidateFormData);
    setEditProfile(false);
  };

  useEffect(() => {
    const tmpGenderPronoun = _.get(profileData, "gender_pronoun", "") || "";
    const genderPronoun =
      CUSTOM_GENDER_OPTIONS.find(
        (row) => row.value === String(tmpGenderPronoun)
      ) || null;

    reset({
      ...profileData,
      gender: getGenderSelectOption(profileData?.gender),
      gender_pronoun: genderPronoun,
    });
  }, [profileData, reset]);

  const onSubmit = async (data: CandidateFormData) => {
    await saveBasicInfo({ ...data });
  };

  // Search location with delay of 1 second
  const searchLocations = (search: string) =>
    new Promise((resolve) => {
      clearTimeout(searchDelay);
      searchDelay = setTimeout(async () => {
        const locationData = await commonServices.fetchLocations(search);

        const locationDataTyped: { data?: { data?: Location[] } } =
          locationData;
        const locations: LocationOption[] = _.get(
          locationDataTyped,
          "data.data",
          []
        ).map((c: Location) => ({
          value: c.id,
          label: c.location,
        }));

        resolve(locations);
      }, 1000);
    });

  const searchJobTitles = (search: string) =>
    new Promise((resolve) => {
      if (search !== "") {
        clearTimeout(searchDelay);
        searchDelay = setTimeout(async () => {
          const jobTitleDetails = await commonServices.fetchJobTitles(search);
          const data = _.get(jobTitleDetails, "data", []) as {
            id: number;
            title: string;
          }[];
          const locations = data.map((c) => ({
            value: c.id,
            label: c.title,
          }));
          resolve(locations);
        }, 1000);
      } else {
        resolve([]);
      }
    });

  const searchGenderPronouns = (search: string) =>
    new Promise((resolve) => {
      const normalizedSearch = (search || "").trim().toLowerCase();
      if (!normalizedSearch) {
        resolve(CUSTOM_GENDER_OPTIONS);
        return;
      }

      const filteredPronouns = CUSTOM_GENDER_OPTIONS.filter((option) =>
        option.label.toLowerCase().includes(normalizedSearch)
      );
      resolve(filteredPronouns);
    });

  const searchWorkStatus = (search: string) =>
    new Promise((resolve) => {
      const normalizedSearch = (search || "").trim().toLowerCase();
      if (!normalizedSearch) {
        resolve(WorkStatusType);
        return;
      }

      const filteredWorkStatus = WorkStatusType.filter((option) =>
        option.label.toLowerCase().includes(normalizedSearch)
      );
      resolve(filteredWorkStatus);
    });

  const searchGenderOptions = (search: string) =>
    new Promise((resolve) => {
      const normalizedSearch = (search || "").trim().toLowerCase();
      if (!normalizedSearch) {
        resolve(genderOptions);
        return;
      }

      const filteredGenderOptions = genderOptions.filter((option) =>
        option.label.toLowerCase().includes(normalizedSearch)
      );
      resolve(filteredGenderOptions);
    });

  const saveBasicInfo = async (formPayload: any) => {
    const formData = new FormData();
    let open_to_other = formPayload.open_to_other === true ? "1" : "0";
    let open_to_remote = formPayload.open_to_remote === true ? "1" : "0";
    let workStatusFilledUpdated = formPayload.work_status?.value || formPayload.work_status || "";

    formData.append("first_name", formPayload.first_name);
    formData.append("last_name", formPayload.last_name);
    formData.append("gender", formPayload.gender?.label?.toLowerCase() || formPayload.gender || "");
    formData.append("gender_optional", formPayload.gender_optional || "");
    formData.append(
      "gender_pronoun",
      _.get(formPayload, "gender_pronoun.value", "")
    );
    formData.append("contact_info", formPayload.contact_info || "");
    formData.append("work_status", workStatusFilledUpdated);

    const jobTitleValue = formPayload.jobTitle?.value;
    const jobTitleLabel = formPayload.jobTitle?.label;

    if (typeof jobTitleValue === "number") {
      formData.append("job_title", jobTitleValue.toString());
      formData.append("job_title_custom", "");
    } else {
      formData.append("job_title", "");
      formData.append("job_title_custom", jobTitleLabel || "");
    }
    formData.append("city_id", formPayload.location?.value?.toString() || "");
    formData.append("open_to_other", open_to_other);
    formData.append("open_to_remote", open_to_remote);
    setSaveLoading(true);
    try {
      const responseData = await candidateServices.updateCandidateBasicInfo(
        formData
      );

      if (_.get(responseData, "flag") === true) {
        const updatedData = {
          first_name: formPayload.first_name,
          last_name: formPayload.last_name,
          gender: formPayload.gender?.label || formPayload.gender || "",
          gender_optional: formPayload.gender_optional || "",
          gender_pronoun: _.get(formPayload, "gender_pronoun.value", 0) || 0,
          contact_info: formPayload.contact_info || "",
          profile: {
            jobTitle: jobTitleValue || jobTitleLabel
              ? {
                id: jobTitleValue || "",
                title: jobTitleLabel || "",
              }
              : null,
            job_title_custom:
              jobTitleValue || !formPayload.jobTitle?.value
                ? ""
                : formPayload.jobTitle?.label || "",
            location: formPayload.location?.value
              ? {
                id: formPayload.location.value,
                location: formPayload.location.label,
              }
              : null,
            work_status: workStatusFilledUpdated,
            open_to_other: open_to_other,
            open_to_remote: open_to_remote,
          },
        };

        sanitizeBody(updatedData);
        dispatch(updateBasicInformation(updatedData));
        dispatch(fetchSessionDataRequest());
        setEditProfile(false);
        FlashMessage(responseData?.message);
      } else {
        const errors = _.get(responseData, "errors", {});
        if (!_.isEmpty(errors)) {
          FlashMessage("Unable to update the Basic information", "error");
          return;
        } else {
          FlashMessage(
            _.get(
              responseData,
              "message",
              "Unable to update the Basic information"
            ),
            "error"
          );
        }
      }
    } catch (error) {
      FlashMessage(
        _.get(error, "message", "Unable to update the Basic information"),
        "error"
      );
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        visibilityControlRef.current &&
        !visibilityControlRef.current.contains(event.target as Node)
      ) {
        setProfileVisibleDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const changeVisibilitySubmit = async (value: number) => {
    if (publicPage) return;
    try {
      const result = await candidateServices.updateCandidateVisibility({
        visibility: value,
      });

      if (result.flag) {
        dispatch(updateCandidateVisibilityRequest(value));
        FlashMessage(result.message, "success");
      } else {
        FlashMessage(result.message, "error");
      }
    } catch (error) {
      FlashMessage("Something went wrong!", "error");
    }
  };

  useEffect(() => {
    let visible = sessionReducer.profile_visibility;
    setVisibilityStatus(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShareProfile = () => {
    setShareProfileModal(true);
  };

  const invitationModalHandleClose = () => setInvitationOpen(false);

  const handleOnInviteSent = async ({ note }: { note?: string }) => {
    try {
      const pendingStatus =
        connectionRequestStatus.find((row) => row.status_text === "pending")
          ?.status_id || 0;
      const candidateId = _.get(candidate, "id", 0);
      if (!candidateId) return;

      const result = await searchAndConnectServices.sendInvitation({
        note: note || "",
        id: candidateId,
      });

      if (_.get(result, "flag", false)) {
        setConnectStatusId(pendingStatus);
        FlashMessage("Invitation sent", "success");
      } else {
        FlashMessage(_.get(result, "message", "Unable to send invitation"), "error");
      }
    } catch (e: any) {
      FlashMessage("Unable to send invitation", "error");
    }
  };

  const handleIncomingConnectionRequest = async (
    type: "accepted" | "rejected"
  ) => {
    const connectionId = _.get(candidate, "follow.id", 0);
    if (!connectionId) {
      FlashMessage("Connection request is unavailable", "error");
      return;
    }

    const statusId = connectionRequestStatus.find(
      (row) => row.status_text === type
    )?.status_id;

    setConnectionActionLoading(true);
    try {
      await searchAndConnectServices.updateCandidateConnection({
        id: connectionId,
        type,
        reject_reason: "",
      });
      setConnectStatusId(statusId || null);
      FlashMessage(
        type === "accepted"
          ? "Connection request accepted"
          : "Connection request declined"
      );
    } catch (error: any) {
      FlashMessage(
        _.get(error, "response.data.message", "Unable to update connection request"),
        "error"
      );
    } finally {
      setConnectionActionLoading(false);
    }
  };

  const renderConnectButton = () => {
    if (!publicPage) return null;
    if (!sessionUserEmail) return null; // hide when not logged in
    if (isOwnProfile) return null;

    if (connectStatusText === "accepted") return null;
    if (connectStatusText === "pending") {
      const isIncomingRequest =
        String(sessionUserId) ===
        String(_.get(candidate, "follow.follower_id", ""));

      if (isIncomingRequest) {
        return (
          <>
            <Button
              color="secondary"
              className="profile-action-btn"
              disabled={connectionActionLoading}
              onClick={() => handleIncomingConnectionRequest("rejected")}
            >
              Decline
            </Button>
            <Button
              color="primary"
              className="profile-action-btn profile-action-btn--primary"
              disabled={connectionActionLoading}
              onClick={() => handleIncomingConnectionRequest("accepted")}
            >
              Accept
            </Button>
          </>
        );
      }

      return (
        <Button disabled color="secondary" className="profile-action-btn">
          Pending
        </Button>
      );
    }
    if (connectStatusText === "rejected") {
      return (
        <Button disabled color="secondary" className="profile-action-btn">
          Rejected
        </Button>
      );
    }
    if (connectStatusText === "cancelled_24_hours") {
      const remainingHours = getRemainingResendHours(
        _.get(candidate, "follow.cancelled_at", "")
      );
      if (remainingHours <= 0) {
        return (
          <Button
            onClick={() => setInvitationOpen(true)}
            color="primary"
            className="profile-action-btn profile-action-btn--primary"
          >
            Connect
          </Button>
        );
      }
      return (
        <Button color="secondary" disabled className="profile-action-btn">
          Wait {remainingHours} hours
        </Button>
      );
    }

    return (
      <Button
        onClick={() => setInvitationOpen(true)}
        color="primary"
        className="profile-action-btn profile-action-btn--primary"
      >
        Connect
      </Button>
    );
  };

  const handleMessageBtnClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!publicPage) return;
    const currentUserId = _.get(sessionReducer, "id", "");
    if (!currentUserId) {
      FlashMessage("Please log in to send a message", "error");
      return;
    }
    if (isOwnProfile) return;
    setMsgAnchorEl(event.currentTarget);
  };

  const handleMessageBtnClose = () => {
    setMsgAnchorEl(null);
  };

  const handleRedirectToMessages = (isCompany = false, companyRow?: any) => {
    const target = candidate || profileData;
    storeNewMessageInfo({
      slug: _.get(target, "slug", ""),
      uuid: _.get(target, "id", 0),
      first_name: _.get(target, "first_name", profileData.first_name),
      isCompany: isCompany ? 1 : null,
      last_name: _.get(target, "last_name", profileData.last_name),
      profile_image: _.get(target, "profile_image", ""),
    });

    if (isCompany) {
      dispatch(tabChangeRequest({ tab: "messages" }));
      navigate(appRoutes.companyMessages.generatePath(companyRow.slug));
    } else {
      navigate(appRoutes.candidateMessages.altPath);
    }

    setMsgAnchorEl(null);
  };

  const _copyProfileLink = () => {
    const profileLink = publicPage
      ? appRoutes.candidatePage.generateFullPath(_.get(profileData, "slug", ""))
      : appRoutes.candidatePublicProfile.generateFullPath(
        _.get(profileData, "slug", "")
      );
    copyToClipboard(profileLink);
    FlashMessage("Copied profile link");
  };

  const handlePublicMenuClose = () => {
    setPublicMenuAnchorEl(null);
  };

  const handlePublicShare = () => {
    _copyProfileLink();
    handlePublicMenuClose();
  };

  const handlePublicReport = () => {
    handlePublicMenuClose();
    const currentUserId = _.get(sessionReducer, "id", "");
    if (!currentUserId) {
      FlashMessage("Please log in to report a profile", "error");
      return;
    }
    if (isOwnProfile) return;
    if (!_.get(candidate, "id")) {
      FlashMessage("Unable to report this profile", "error");
      return;
    }
    setShowReportModal(true);
  };

  const reportProfileReferenceId = String(_.get(candidate, "id", "") || "");
  const genderDisplayLabel = getGenderLabelFromProfile(profileData);
  const fullName = [profileData?.first_name, profileData?.last_name]
    .filter(Boolean)
    .join(" ");
  const jobTitleLabel = profileData?.jobTitle?.label || "";
  const locationLabel = profileData?.location?.label || "";
  const workStatusLabel =
    typeof profileData?.work_status === "object"
      ? profileData?.work_status?.label || ""
      : profileData?.work_status || "";
  const isOpenToRemote = Number(profileData?.open_to_remote) === 1;
  const isOpenToOther = Number(profileData?.open_to_other) === 1;
  const profileMetaItems = [
    locationLabel && { key: "location", label: locationLabel, icon: MapPinIcon },
    workStatusLabel && { key: "work-status", label: workStatusLabel },
    genderDisplayLabel && { key: "gender", label: genderDisplayLabel },
  ].filter(Boolean) as Array<{ key: string; label: string; icon?: string }>;
  const profileBadges = [
    isOpenToRemote && "Open to remote",
    isOpenToOther && "Open to opportunities",
  ].filter(Boolean) as string[];
  const showProfileMeta = profileMetaItems.length > 0 || profileBadges.length > 0;

  return (
    <div className="user-info">
      <div className="user-info-avatar">
        <ProfileImage
          profileImageData={profileImageData}
          isPublicPage={publicPage}
          openUploadRequest={openMediaUploadRequest}
          onOpenUploadHandled={onOpenMediaUploadHandled}
        />
      </div>

      <div className="user-info-details">
        <div className="profile-content">
          <h4 className="profile-name">{fullName || "Profile"}</h4>

          {jobTitleLabel ? (
            <p className="profile-headline">{jobTitleLabel}</p>
          ) : (
            !publicPage && (
              <button
                type="button"
                className="profile-headline profile-headline-placeholder"
                onClick={openProfileModal}
              >
                Add a headline
              </button>
            )
          )}

          {showProfileMeta && (
            <div className="profile-meta-row">
              {profileMetaItems.length > 0 && (
                <div className="profile-meta">
                  {profileMetaItems.map((item, index) => (
                    <React.Fragment key={item.key}>
                      {index > 0 && (
                        <span className="profile-meta-separator" aria-hidden="true">
                          ·
                        </span>
                      )}
                      <span className={`profile-meta-item profile-meta-${item.key}`}>
                        {item.icon && (
                          <img
                            src={item.icon}
                            alt=""
                            aria-hidden="true"
                            className="profile-meta-icon"
                          />
                        )}
                        <span>{item.label}</span>
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              )}

              {profileBadges.length > 0 && (
                <div className="profile-badges">
                  {profileBadges.map((badge) => (
                    <span key={badge} className="profile-badge">
                      {badge}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {!publicPage && !showProfileMeta && (
            <button
              type="button"
              className="profile-complete-link"
              onClick={openProfileModal}
            >
              Add location, work status, and more
            </button>
          )}
        </div>
      </div>

      <div className="profile-actions share-profile">
        {!publicPage && (
          <>
            <Button
              variant="outlined"
              color="secondary"
              className="profile-action-btn"
              onClick={openProfileModal}
            >
              Edit profile
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              className="profile-action-btn"
              onClick={handleShareProfile}
            >
              Share
            </Button>
            <div className="profile-visibility-control" ref={visibilityControlRef}>
              <button
                type="button"
                className="profile-action-btn profile-action-btn--visibility"
                aria-expanded={profileVisibleDropdown}
                aria-haspopup="listbox"
                onClick={() => setProfileVisibleDropdown(!profileVisibleDropdown)}
              >
                <span>
                  {Number(visibilityStatus) === 0
                    ? "Connections only"
                    : Number(visibilityStatus) === 1
                      ? "Public"
                      : "Only me"}
                </span>
                <img src={Dropdown} alt="" aria-hidden="true" />
              </button>
              {profileVisibleDropdown && (
                <div className="dropdown-content-wrapper">
                  <RadioGroup
                    value={String(visibilityStatus)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const nextValue = Number(e.target.value);
                      setVisibilityStatus(nextValue);
                      setProfileVisibleDropdown(false);
                      changeVisibilitySubmit(nextValue);
                    }}
                    aria-label="Profile visibility"
                    name="profile-visibility"
                  >
                    <FormControlLabel
                      value="1"
                      control={<Radio color="secondary" />}
                      label="Public"
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="0"
                      control={<Radio color="secondary" />}
                      label="Connections only"
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="2"
                      control={<Radio color="secondary" />}
                      label="Only me"
                      labelPlacement="end"
                    />
                  </RadioGroup>
                </div>
              )}
            </div>
          </>
        )}

        {publicPage && !isOwnProfile && (
          <>
            <div className="profile-action-primary">{renderConnectButton()}</div>
            {(sessionReducer?.companies?.length > 0 || connectAccepted) &&
              profileMessagingEnabled && (
                <Button
                  onClick={handleMessageBtnClick}
                  color="secondary"
                  variant="outlined"
                  className="profile-action-btn"
                  disabled={!sessionUserEmail}
                >
                  Message
                </Button>
              )}
          </>
        )}

        {publicPage && (
          <button
            type="button"
            className="profile-more-btn"
            aria-label="More options"
            onClick={(e) => setPublicMenuAnchorEl(e.currentTarget)}
          >
            <img src={dots} alt="" aria-hidden="true" />
          </button>
        )}
      </div>

      {publicPage && (
        <>
          <Menu
            id="public-profile-menu"
            anchorEl={publicMenuAnchorEl}
            keepMounted
            open={Boolean(publicMenuAnchorEl)}
            onClose={handlePublicMenuClose}
            classes={{ paper: "border-radius-md" }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem onClick={handlePublicShare}>Share</MenuItem>
            {!isOwnProfile && (
              <MenuItem onClick={handlePublicReport}>Report profile</MenuItem>
            )}
          </Menu>
          {!isOwnProfile && showReportModal && reportProfileReferenceId && (
            <Report
              openReports={true}
              referenceId={reportProfileReferenceId}
              type="candidate_profile"
              onClose={() => setShowReportModal(false)}
            />
          )}
        </>
      )}

      {publicPage && (
        <InvitationModal
          open={invitationOpen}
          handleOnClose={invitationModalHandleClose}
          onInviteSent={handleOnInviteSent}
          payload={candidate || profileData}
        />
      )}

      {publicPage && (
        <Menu
          id="message-menu"
          anchorEl={msgAnchorEl}
          keepMounted
          open={Boolean(msgAnchorEl)}
          onClose={handleMessageBtnClose}
          classes={{ paper: "border-radius-md" }}
        >
          {(sessionReducer?.companies || []).map((row: any, key: number) => (
            <MenuItem
              key={key}
              onClick={() => handleRedirectToMessages(true, row)}
            >
              as {row?.company_name}
            </MenuItem>
          ))}
          {connectAccepted && (
            <MenuItem onClick={() => handleRedirectToMessages(false)}>
              as{" "}
              {`${sessionReducer?.first_name || ""} ${sessionReducer?.last_name || ""}`.trim()}
            </MenuItem>
          )}
        </Menu>
      )}

      <Modal
        className="candidate-profile-modal basic-information"
        isOpen={editProfile}
        onClose={closeProfileModal}
        title="Basic Information"
        visible={editProfile}
        size="medium"
        onRendered={() => {
          void trigger();
        }}
      >
        <div className="modal-body-wrapper">
          <form className="form-wrapper" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <div className="form-group">
                  <Input
                    inputIcon={
                      <div className="input-icon">
                        <img src={NameIcon} alt="icon" />
                      </div>
                    }
                    className="input-with-icon"
                    defaultValue={profileData?.first_name}
                    externalLabel={{ label: "First Name", className: "required" }}
                    placeholder="First name"
                    validationObj={errors}
                    {...register("first_name", {
                      required: {
                        value: true,
                        message: "Please enter the name.",
                      },
                    })}
                  />
                </div>
              </Grid>
              <Grid item sm={6} xs={12}>
                <div className="form-group">
                  <Input
                    inputIcon={
                      <div className="input-icon">
                        <img src={NameIcon} alt="icon" />
                      </div>
                    }
                    className="input-with-icon"
                    defaultValue={profileData?.last_name}
                    externalLabel={{ label: "Last Name", className: "required" }}
                    placeholder="Last name"
                    validationObj={errors}
                    {...register("last_name", {
                      required: {
                        value: true,
                        message: "Please enter the name.",
                      },
                    })}
                  />
                </div>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <div className="form-group">
                  <Input
                    inputIcon={
                      <div className="input-icon">
                        <img src={EmailIcon} alt="icon" />
                      </div>
                    }
                    className="input-with-icon"
                    defaultValue={profileData?.email}
                    externalLabel={{ label: "Email Address", className: "required" }}
                    placeholder="Email Address"
                    validationObj={errors}
                    {...register("email", {
                      required: {
                        value: true,
                        message: "Please enter the email address.",
                      },
                    })}
                  />
                </ div>
              </Grid>
              <Grid item sm={6} xs={12}>
                <div className={`form-group phone-wrapper${errors.contact_info ? " has-error" : ""}`}>
                  <label className="form-label required">Phone Number</label>
                  <EditableText
                    inputClassName="editable-input"
                    control={control}
                    name="contact_info"
                    defaultValue={profileData?.contact_info}
                    country="us"
                    placeholder="Phone Number"
                    icon={<img src={PhoneIcon} alt="" />}
                    error={errors.contact_info}
                    rules={{
                      validate: validatePhoneNumber,
                    }}
                    wrapperClassName="phone-wrapper"
                  />
                </div>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <div className="form-group">
                  <AsyncSelect
                    name="location"
                    placeholder="Select your Work Location"
                    onStartSearching={searchLocations}
                    icon={<img src={LocationIcon} alt="" />}
                    externalLabel={{
                      label: "Location",
                      className: "form-label required",
                    }}
                    validationObj={errors}
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: "Select your Work Location",
                      },
                    }}
                  />
                </div>
              </Grid>
              <Grid item sm={6} xs={12}>
                <div className="form-group">
                  <AsyncSelect
                    name="jobTitle"
                    control={control}
                    validationObj={errors}
                    placeholder="Search Job..."
                    icon={<img src={JobTitleIcon} alt="" />}
                    externalLabel={{
                      label: "Job Title",
                      className: "required",
                    }}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select job title",
                      },
                    }}
                    onStartSearching={searchJobTitles}
                    noOptionsMessage="No matching job found"
                  />
                </div>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <Checkbox
                  externalLabel={{
                    label: "Open to remote opportunities",
                  }}
                  color="primary"
                  {...register("open_to_remote")}
                  defaultChecked={
                    Number(_.get(profileData, "open_to_remote")) === 1
                      ? true
                      : false
                  }
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Checkbox
                  externalLabel={{
                    label: "Open to other opportunities",
                  }}
                  color="primary"
                  {...register("open_to_other")}
                  defaultChecked={
                    Number(_.get(profileData, "open_to_other")) === 1
                      ? true
                      : false
                  }
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <div className="form-group">
                  <AsyncSelect
                    name="work_status"
                    externalLabel={{ label: "Work Status", className: "required" }}
                    placeholder="Work status"
                    options={WorkStatusType}
                    icon={<img src={LocationIcon} alt="" />}
                    onStartSearching={searchWorkStatus}
                    validationObj={errors}
                    control={control}
                    required={true}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select work status",
                      },
                    }}
                  />
                </div>
              </Grid>
            </Grid>
            <div className="form-group gender-select-box">
              <AsyncSelect
                name="gender"
                placeholder="She/Her/Hers"
                options={genderOptions}
                icon={<img src={GenderIcon} alt="" />}
                search={false}
                onStartSearching={searchGenderOptions}
                handleCustomOnChange={(selectedGender: {
                  value?: string;
                  label?: string;
                } | null) => {
                  const raw =
                    selectedGender?.value ?? selectedGender?.label ?? "";
                  const selectedGenderValue = String(raw).toLowerCase();

                  if (selectedGenderValue === "custom") {
                    setValue("gender_pronoun", null, {
                      shouldValidate: false,
                    });
                    clearErrors(["gender_pronoun", "gender_optional"]);
                    void trigger(["gender", "gender_pronoun", "gender_optional"]);
                  } else {
                    setValue("gender_pronoun", null, {
                      shouldValidate: false,
                    });
                    setValue("gender_optional", "", {
                      shouldValidate: false,
                    });
                    unregister(["gender_pronoun", "gender_optional"]);
                    clearErrors(["gender_pronoun", "gender_optional"]);
                    void trigger();
                  }
                }}
                externalLabel={{
                  label: "Gender",
                  className: "form-label required",
                }}
                validationObj={errors}
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: "Select your gender",
                  },
                }}
              />
            </div>
            {watch("gender") && isCustomGender(watch("gender")) && (
              <>
                <div className="form-group gender-pronoun">
                  <AsyncSelect
                    name="gender_pronoun"
                    placeholder="Select your pronoun"
                    options={CUSTOM_GENDER_OPTIONS}
                    icon={<img src={GenderIcon} alt="" />}
                    search={false}
                    onStartSearching={searchGenderPronouns}
                    validationObj={errors}
                    control={control}
                    rules={{
                      validate: (
                        value: { value?: string | number; label?: string } | null
                      ) => {
                        if (isCustomGender(watch("gender")) && !value) {
                          return "Please select your pronoun";
                        }
                        return true;
                      },
                    }}
                  />
                </div>
                <div>
                  <Input
                    size="small"
                    placeholder="Customize your pronoun"
                    validationObj={errors}
                    {...register("gender_optional", {
                      validate: (value) => {
                        if (
                          isCustomGender(watch("gender")) &&
                          (!value || value.trim() === "")
                        ) {
                          return "Please customize your pronoun";
                        }
                        return true;
                      },
                    })}
                  />
                </div>
              </>
            )}
            <div className="btn-wrapper">
              <Button
                variant="outlined"
                className="outlined"
                onClick={closeProfileModal}
                color="none"
              >
                Cancel
              </Button>
              <Button
                className="save-btn"
                type="submit"
                loading={saveLoading}
                disabled={!isValid || saveLoading}
              >
                Save
              </Button>
            </div>
          </form>
        </div>
      </Modal>
      <ProfileShareModal
        open={shareProfileModal}
        onClose={() => setShareProfileModal(false)}
        copyProfileLink={_copyProfileLink}
        publicProfileLink={
          publicPage
            ? appRoutes.candidatePage.generateFullPath(
              _.get(profileData, "slug", "")
            )
            : appRoutes.candidatePublicProfile.generateFullPath(
              _.get(profileData, "slug", "")
            )
        }
      />
    </div>
  );
}

export default CandidateInfo;
